/**
 * Dialog para criar nova credencial segura
 * Suporta diferentes tipos: API Key, Basic Auth, Bearer Token, Custom
 * 
 * RELEASE: 1.0.0 - 2024-12-24
 */

import { useState } from 'react';
import { Eye, EyeOff, Key, Lock, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useTenantCredentials } from '@/hooks/useTenantCredentials';
import { 
  CredentialType, 
  KeyLocation,
  ApiKeyCredentialData,
  BasicAuthCredentialData,
  BearerTokenCredentialData,
  CustomCredentialData,
  CREDENTIAL_TYPE_OPTIONS,
  KEY_LOCATION_OPTIONS,
} from '@/types/credentials';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('CreateCredentialDialog');

interface CreateCredentialDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para alterar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** ID do tenant atual */
  tenantId: string;
  /** Callback quando credencial é criada com sucesso */
  onSuccess?: (credentialId: string) => void;
}

/**
 * Dialog para criar nova credencial
 */
export function CreateCredentialDialog({
  open,
  onOpenChange,
  tenantId,
  onSuccess,
}: CreateCredentialDialogProps) {
  // Estados do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [credentialType, setCredentialType] = useState<CredentialType>('api_key');
  
  // Estados para API Key
  const [apiKeyName, setApiKeyName] = useState('api_key');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [apiKeyLocation, setApiKeyLocation] = useState<KeyLocation>('body');
  const [showApiKeyValue, setShowApiKeyValue] = useState(false);
  
  // Estados para Basic Auth
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [showBasicPassword, setShowBasicPassword] = useState(false);
  
  // Estados para Bearer Token
  const [bearerToken, setBearerToken] = useState('');
  const [showBearerToken, setShowBearerToken] = useState(false);
  
  // Estados para Custom
  // Estado para campos customizados com destino de injeção individual
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string; location: KeyLocation }>>([
    { key: '', value: '', location: 'header' }
  ]);

  const { createCredentialAsync, isCreating } = useTenantCredentials(tenantId);

  /**
   * Reseta o formulário
   */
  const resetForm = () => {
    setName('');
    setDescription('');
    setCredentialType('api_key');
    setApiKeyName('api_key');
    setApiKeyValue('');
    setApiKeyLocation('body');
    setShowApiKeyValue(false);
    setBasicUsername('');
    setBasicPassword('');
    setShowBasicPassword(false);
    setBearerToken('');
    setShowBearerToken(false);
    setCustomFields([{ key: '', value: '', location: 'header' }]);
  };

  /**
   * Handler para fechar o dialog
   */
  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  /**
   * Adiciona campo customizado
   */
  const addCustomField = () => {
    setCustomFields([...customFields, { key: '', value: '', location: 'header' }]);
  };

  /**
   * Remove campo customizado
   */
  const removeCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  /**
   * Atualiza campo customizado
   */
  /**
   * Atualiza campo customizado (nome, valor ou destino de injeção)
   */
  const updateCustomField = (index: number, field: 'key' | 'value' | 'location', value: string) => {
    const updated = [...customFields];
    if (field === 'location') {
      updated[index].location = value as KeyLocation;
    } else {
      updated[index][field] = value;
    }
    setCustomFields(updated);
  };

  /**
   * Valida e submete o formulário
   */
  const handleSubmit = async () => {
    // Validação do nome
    if (!name.trim()) {
      toast.error('Nome da credencial é obrigatório');
      return;
    }

    // Monta o objeto de credenciais baseado no tipo
    let credentials: ApiKeyCredentialData | BasicAuthCredentialData | BearerTokenCredentialData | CustomCredentialData;

    switch (credentialType) {
      case 'api_key':
        if (!apiKeyName.trim() || !apiKeyValue.trim()) {
          toast.error('Nome e valor da API Key são obrigatórios');
          return;
        }
        credentials = {
          key_name: apiKeyName.trim(),
          key_value: apiKeyValue.trim(),
          key_location: apiKeyLocation,
        } as ApiKeyCredentialData;
        break;

      case 'basic_auth':
        if (!basicUsername.trim() || !basicPassword.trim()) {
          toast.error('Usuário e senha são obrigatórios');
          return;
        }
        credentials = {
          username: basicUsername.trim(),
          password: basicPassword.trim(),
        } as BasicAuthCredentialData;
        break;

      case 'bearer_token':
        if (!bearerToken.trim()) {
          toast.error('Token é obrigatório');
          return;
        }
        credentials = {
          token: bearerToken.trim(),
        } as BearerTokenCredentialData;
        break;

      case 'custom':
        const validFields = customFields.filter(f => f.key.trim() && f.value.trim());
        if (validFields.length === 0) {
          toast.error('Pelo menos um campo customizado é obrigatório');
          return;
        }
        // Gera fields e field_locations separados para retrocompatibilidade
        credentials = {
          fields: Object.fromEntries(validFields.map(f => [f.key.trim(), f.value.trim()])),
          field_locations: Object.fromEntries(validFields.map(f => [f.key.trim(), f.location])),
        } as CustomCredentialData;
        break;
    }

    try {
      log.debug('Criando credencial:', { name, type: credentialType });
      
      const result = await createCredentialAsync({
        name: name.trim(),
        credential_type: credentialType,
        credentials,
        description: description.trim() || undefined,
      });

      log.info('Credencial criada com sucesso:', result.id);
      handleClose();
      onSuccess?.(result.id);
    } catch (error) {
      log.error('Erro ao criar credencial:', error);
      // Toast já é exibido pelo hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Nova Credencial
          </DialogTitle>
          <DialogDescription>
            Cadastre uma credencial segura para usar em webhooks e APIs
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nome da credencial */}
          <div className="space-y-2">
            <Label htmlFor="cred-name">Nome *</Label>
            <Input
              id="cred-name"
              placeholder="Ex: API W2SAT Produção"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              Nome para identificar esta credencial
            </p>
          </div>

          {/* Tipo de credencial */}
          <div className="space-y-2">
            <Label>Tipo de Autenticação *</Label>
            <Select 
              value={credentialType} 
              onValueChange={(v) => setCredentialType(v as CredentialType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CREDENTIAL_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Campos dinâmicos por tipo */}
          <div className="border-t pt-4 space-y-4">
            {/* API Key */}
            {credentialType === 'api_key' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="api-key-name">Nome do campo *</Label>
                  <Input
                    id="api-key-name"
                    placeholder="Ex: api_key, X-Api-Key, Authorization"
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Nome do parâmetro esperado pela API
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="api-key-value">Valor da API Key *</Label>
                  <div className="relative">
                    <Input
                      id="api-key-value"
                      type={showApiKeyValue ? 'text' : 'password'}
                      placeholder="••••••••••••••••"
                      value={apiKeyValue}
                      onChange={(e) => setApiKeyValue(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowApiKeyValue(!showApiKeyValue)}
                    >
                      {showApiKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Onde inserir?</Label>
                  <RadioGroup 
                    value={apiKeyLocation} 
                    onValueChange={(v) => setApiKeyLocation(v as KeyLocation)}
                  >
                    {KEY_LOCATION_OPTIONS.map((opt) => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={`loc-${opt.value}`} />
                        <Label htmlFor={`loc-${opt.value}`} className="flex flex-col cursor-pointer">
                          <span>{opt.label}</span>
                          <span className="text-xs text-muted-foreground font-normal">
                            {opt.description}
                          </span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </>
            )}

            {/* Basic Auth */}
            {credentialType === 'basic_auth' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="basic-user">Usuário *</Label>
                  <Input
                    id="basic-user"
                    placeholder="username"
                    value={basicUsername}
                    onChange={(e) => setBasicUsername(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="basic-pass">Senha *</Label>
                  <div className="relative">
                    <Input
                      id="basic-pass"
                      type={showBasicPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={basicPassword}
                      onChange={(e) => setBasicPassword(e.target.value)}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowBasicPassword(!showBasicPassword)}
                    >
                      {showBasicPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Bearer Token */}
            {credentialType === 'bearer_token' && (
              <div className="space-y-2">
                <Label htmlFor="bearer-token">Token *</Label>
                <div className="relative">
                  <Input
                    id="bearer-token"
                    type={showBearerToken ? 'text' : 'password'}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6..."
                    value={bearerToken}
                    onChange={(e) => setBearerToken(e.target.value)}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowBearerToken(!showBearerToken)}
                  >
                    {showBearerToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Será enviado como: Authorization: Bearer [token]
                </p>
              </div>
            )}

            {/* Custom Fields */}
            {credentialType === 'custom' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Campos Customizados *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCustomField}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Adicionar Campo
                  </Button>
                </div>

                {/* Cabeçalho das colunas */}
                <div className="flex gap-2 items-center text-xs text-muted-foreground">
                  <div className="flex-1">Nome do campo</div>
                  <div className="flex-1">Valor</div>
                  <div className="w-[120px]">Onde inserir?</div>
                  <div className="w-8" />
                </div>

                {customFields.map((field, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Input
                        placeholder="Ex: token_api"
                        value={field.key}
                        onChange={(e) => updateCustomField(index, 'key', e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="password"
                        placeholder="Valor"
                        value={field.value}
                        onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                      />
                    </div>
                    {/* Seletor de destino de injeção por campo */}
                    <div className="w-[120px]">
                      <Select
                        value={field.location}
                        onValueChange={(v) => updateCustomField(index, 'location', v)}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="header">Header</SelectItem>
                          <SelectItem value="body">Body</SelectItem>
                          <SelectItem value="query">Query</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {customFields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCustomField(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Descrição opcional */}
          <div className="space-y-2 border-t pt-4">
            <Label htmlFor="cred-desc">Descrição (opcional)</Label>
            <Textarea
              id="cred-desc"
              placeholder="Ex: Credencial para consultar boletos no sistema W2SAT"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Aviso de segurança */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <p className="text-sm text-primary flex items-start gap-2">
              <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Suas credenciais são armazenadas de forma segura e nunca são expostas no frontend.
                Apenas são usadas no momento da execução do webhook.
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating}>
            {isCreating ? 'Salvando...' : 'Salvar Credencial'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
